import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { ArrowLeft, Calendar, Clock, Sparkles } from "lucide-react";

interface FormData {
  workDate: string;
  hoursWorked: number | "";
  creditDays: number | "";
  reason: string;
}

export default function ApplyCompOff() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<FormData>({
    workDate: "",
    hoursWorked: "",
    creditDays: 1,
    reason: "",
  });

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getTodayDate = () => new Date().toISOString().split("T")[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      if (
        !formData.workDate ||
        formData.hoursWorked === "" ||
        formData.creditDays === "" ||
        !formData.reason.trim()
      ) {
        setError("Please fill in all required fields");
        return;
      }

      if (Number(formData.hoursWorked) <= 0 || Number(formData.creditDays) <= 0) {
        setError("Hours worked and credit days must be greater than 0");
        return;
      }

      const token = localStorage.getItem("authToken");
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/compoff/apply`,
        {
          workDate: formData.workDate,
          hoursWorked: Number(formData.hoursWorked),
          creditDays: Number(formData.creditDays),
          reason: formData.reason.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      navigate("/employee/compoff", {
        state: { message: "Comp-off request submitted for approval!" },
      });
    } catch (err: any) {
      console.error("Error applying comp-off:", err);
      setError(err.response?.data?.message || "Failed to submit comp-off");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/employee/compoff")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            Apply for Comp-Off
          </h2>
          <p className="text-muted-foreground">
            Request compensatory off for working on your off day
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      <Card className="border-2 bg-purple-50 border-purple-200">
        <CardContent className="pt-6 flex items-start gap-3">
          <Sparkles className="h-8 w-8 text-purple-600" />
          <div>
            <p className="font-semibold text-purple-800">
              How comp-off credits work
            </p>
            <ul className="text-sm text-purple-900 mt-2 list-disc pl-4 space-y-1">
              <li>Use comp-off only after admin approval.</li>
              <li>
                You can consume approved comp-off credits while applying for
                leaves.
              </li>
              <li>
                Approved credits will be exhausted automatically once the
                related leave is approved.
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Work Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3">
              <Label htmlFor="workDate">
                Work Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="workDate"
                type="date"
                value={formData.workDate}
                onChange={(e) => handleInputChange("workDate", e.target.value)}
                max={getTodayDate()}
                disabled={isLoading}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-3">
                <Label htmlFor="hoursWorked">
                  Hours Worked <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="hoursWorked"
                    type="number"
                    min={1}
                    step={0.5}
                    value={formData.hoursWorked}
                    onChange={(e) =>
                      handleInputChange("hoursWorked", Number(e.target.value))
                    }
                    disabled={isLoading}
                    required
                  />
                  <Clock className="h-4 w-4 text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter total hours worked on the day off
                </p>
              </div>

              <div className="grid gap-3">
                <Label htmlFor="creditDays">
                  Credit Days <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="creditDays"
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={formData.creditDays}
                  onChange={(e) =>
                    handleInputChange("creditDays", Number(e.target.value))
                  }
                  disabled={isLoading}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Typically 1 day for a full workday
                </p>
              </div>
            </div>

            <div className="grid gap-3">
              <Label htmlFor="reason">
                Work Summary <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="reason"
                placeholder="Describe what you worked on"
                value={formData.reason}
                onChange={(e) => handleInputChange("reason", e.target.value)}
                disabled={isLoading}
                required
                rows={4}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/employee/compoff")}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Spinner variant="ellipsis" size={20} />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  "Submit Request"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

